import { useCallback, useEffect, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { PrintStatus } from "../../api/types";

/** How often the till asks again: whether a Tafo app runs changes without a live message. */
const POLL_MS = 30_000;

/**
 * The state of printing for the top bar (blok I): how many printers, which computers run the Tafo app
 * for Windows, what waits and what was not printed in the last hour. Asked every 30 seconds and after a
 * live message (`reload`, which resolves with the fresh state); a failed request keeps the last state.
 */
export function usePrintStatus(
  api: ApiClient,
  on: boolean,
): { status: PrintStatus | null; reload: () => Promise<PrintStatus | null> } {
  const [status, setStatus] = useState<PrintStatus | null>(null);
  const fetchStatus = useCallback(
    () => api.request<PrintStatus>("GET", "/device/print-status", { retry: true }),
    [api],
  );

  useEffect(() => {
    if (!on) return;
    let stopped = false;
    const load = () => {
      fetchStatus()
        .then((fresh) => {
          if (!stopped) setStatus(fresh);
        })
        // extra information: the till works without it, and the next poll tries again
        .catch(() => undefined);
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [fetchStatus, on]);

  const reload = useCallback(async () => {
    if (!on) return null;
    try {
      const fresh = await fetchStatus();
      setStatus(fresh);
      return fresh;
    } catch {
      return null;
    }
  }, [fetchStatus, on]);

  return { status, reload };
}
